import { getJobEvents, getOfficials } from "@/api";
import { computed, InjectionKey, ref, watch } from "@vue/composition-api";
import { useComponentInstance } from "./useComponentInstance";
import { useRequest } from "./useRequest";
import { getRandomId } from "@/helpers/math-helper";
import { RouterLocation } from "../router-helper";
import {
  getPartnerUserAndContracts,
  hasEventApprovedPolicy
} from "../auth-helper";

import {
  hasPrivilege,
  PRIVILEGE_OFFERS_READ,
  PRIVILEGE_OFFERS_WRITE
} from "../privilege-helper";

import { ApiResponse } from "@/api/types";

type GlobalMessageStore = ReturnType<typeof useGlobalMessage>;

/**
 * inject用のKey
 */
export const GLOBAL_MESSAGE: InjectionKey<GlobalMessageStore> = Symbol(
  "GLOBAL_MESSAGE"
);

type Message = {
  message: string;
  key: string;
  onClick?: () => void;
  exclusionPages?: RouterLocation[];
};

/**
 * グローバルメッセージの状態を保管する
 */
export const useGlobalMessage = () => {
  const root = useComponentInstance();
  const messages = ref<Message[]>([]);
  const isLoggedIn = computed<ApiResponse.PartnerUser | undefined>(
    () => root.$store.getters["domains/user/isLoggedIn"]
  );

  /**
   * アプリ起動時に必要なメッセージを表示する
   */
  const initializeMessages = () => {
    const [{ data: rejectedJobEvents }, runGetRejectedJobEvents] = useRequest(
      () =>
        getJobEvents({
          approval_statuses: ["rejected"],
          sort: "updated_at",
          order: "asc"
        })
    );

    const [{ data: unlotteryJobEvents }, runGetUnlotteryJobEvents] = useRequest(
      () =>
        getJobEvents({
          unlottery: true,
          sort: "updated_at",
          order: "asc",
          limit: 1
        })
    );

    const [{ data: publicJobEvents }, runGetPublicJobEvents] = useRequest(() =>
      getJobEvents({
        public_statuses: ["public"],
        sort: "updated_at",
        order: "asc",
        limit: 1
      })
    );

    const [{ data: officialsData }, runGetOfficials] = useRequest(() =>
      getOfficials()
    );

    const pushRejectedOfficialsMessage = async () => {
      await runGetOfficials();

      if (
        !officialsData.value ||
        !officialsData.value.officials ||
        officialsData.value.officials.length === 0
      )
        return;
      const rejectedOfficials = officialsData.value.officials.filter(
        official => {
          return official.approval_status === "rejected";
        }
      );

      if (rejectedOfficials.length === 0) return;
      const latestRejectedOfficial = rejectedOfficials.sort((before, after) => {
        return after.id - before.id;
      })[0];
      const hasNewOfficial = officialsData.value.officials.find(official => {
        return (
          official.contract_id === latestRejectedOfficial.contract_id &&
          official.id > latestRejectedOfficial.id
        );
      });

      if (!hasNewOfficial && hasPrivilege(PRIVILEGE_OFFERS_WRITE)) {
        push(
          `企業ページが棄却されています。該当箇所を修正して再度承認申請を行ってください。`,
          () => {
            root.$router
              .push({
                name: "OfficialEdit",
                query: {
                  id: String(latestRejectedOfficial.id)
                }
              })
              .catch(err => err);
          },
          [{ name: "OfficialEdit" }]
        );
      }
    };

    const displayedRejectedJobEvents = computed(
      () =>
        rejectedJobEvents.value?.events?.filter(
          event => !event.is_pre_entry_event
        ) || []
    );

    watch(
      isLoggedIn,
      async currentIsLoggedIn => {
        if (currentIsLoggedIn) {
          await getPartnerUserAndContracts();
          if (hasPrivilege(PRIVILEGE_OFFERS_READ)) {
            // 募集内容の閲覧権限がある場合
            await Promise.all([
              pushRejectedOfficialsMessage(),
              runGetRejectedJobEvents(),
              runGetUnlotteryJobEvents(),
              runGetPublicJobEvents()
            ]);

            if (displayedRejectedJobEvents.value.length) {
              push(
                `棄却されたイベントがあります。該当箇所を修正して再度承認申請を行ってください。`,
                () => {
                  root.$router
                    .push({
                      name: "JobEvents",
                      query: {
                        approval_statuses: ["rejected"]
                      }
                    })
                    .catch(err => err);
                }
              );
            }

            if (unlotteryJobEvents.value?.events?.length) {
              push(`未抽選のイベントがあります。抽選を行ってください。`, () => {
                root.$router
                  .push({
                    name: "JobEvents",
                    query: {
                      unlottery: "1"
                    }
                  })
                  .catch(err => err);
              });
            }

            if (hasEventApprovedPolicy()) {
              // イベント作成ができるポリシーを持つ有効な契約が存在する
              if (hasPrivilege(PRIVILEGE_OFFERS_WRITE)) {
                // イベントの書き込み権限がある
                if (publicJobEvents.value?.events?.length === 0) {
                  push(
                    `イベントが公開されていません。イベントを作成して応募を集めましょう。`,
                    () => {
                      root.$router
                        .push({
                          name: "JobEvents"
                        })
                        .catch(err => err);
                    }
                  );
                }
              }
            }
          }
        } else {
          messages.value = [];
        }
      },
      { immediate: true }
    );
  };

  initializeMessages();

  const push = (
    message: string,
    onClick?: (param: { hide: () => void }) => void,
    exclusionPages?: RouterLocation[],
    key: string = String(getRandomId())
  ) => {
    messages.value = [
      ...messages.value,
      {
        message,
        onClick: onClick ? () => onClick({ hide: () => hide(key) }) : undefined,
        key,
        exclusionPages: exclusionPages
      }
    ];
  };

  const hide = (key: string) => {
    messages.value = messages.value.filter(message => message.key !== key);
  };

  return { messages, push, hide } as const;
};
